/**
 * ROULETTE-NUMBERS-ORDER-CONFIG.JS - Tabla de tipos de rueda (SSOT de datos).
 *
 * Expone `ROULETTE_TYPES`: por cada receta, un shape rico que los consumers
 * (factory money-path, contracts, mat factory, wheel renderer) leen. Cada
 * campo es proyeccion directa de la receta o derivacion geometrica via
 * `lector-de-rueda`. Cero bridge intermedio.
 *
 * Este módulo es SOLO DATOS. Para operar sobre una rueda (cobertura,
 * multiplicadores, colores) usar la factory `crearConfiguracionDeRuedaParaTipo`
 * (`rueda-config-factory.js`) — sin estado global.
 */

import { RECETAS_AGNOSTICAS } from './recetas-agnosticas.js';
import {
    sectoresConArcoYesCero,
    matGridDeReceta,
    geometriaDeReceta,
} from '../../../lotowheel-render/lector-de-rueda.js';

// =============================================================================
// CEROS (LOTO)
// =============================================================================
export const LOTO = { value: 0, displayValue: 37, label: 'LOTO', color: '#22bf08' };
export const LOTO_PLUS = { value: 37, displayValue: 38, label: 'LOTO', color: '#22bf08' };
export const LOTO_PLUS_PLUS = { value: 38, displayValue: 39, label: 'LOTO', color: '#22bf08' };

// =============================================================================
// MODOS DE PRESENTACIÓN DEL CERO
// =============================================================================
// Cómo se muestra un sector de cero, según lo que el operador configuró en el
// sorteo (`zero_display_mode`). La lógica que los aplica vive en
// `resolver-ceros-de-rueda.js`.
export const ZERO_DISPLAY_MODES = Object.freeze({
    POR_NUMERO: 'POR_NUMERO',
    POR_CEROS: 'POR_CEROS',
    POR_TEXTO: 'POR_TEXTO',
});

// Labels por defecto del cero en modo POR_TEXTO, por posición del cero.
export const DEFAULT_ZERO_LABELS = Object.freeze(['LOTO', 'LOTO+', 'LOTO++']);

// =============================================================================
// TIPOS DE RUEDA
// =============================================================================
//
// Agregar una rueda nueva = una entrada más en `recetas-agnosticas.js`. Aparece
// automaticamente en `ROULETTE_TYPES` sin tocar nada mas.
//
// Congela en profundidad: `ROULETTE_TYPES` se construye una vez al import y lo
// comparten N sorteos del proceso. Un consumidor que mute `config.order` o
// `config.matGrid` corromperia el SSOT para todos — el freeze profundo cierra
// esa puerta.
function congelarEnProfundidad(valor) {
    if (valor && typeof valor === 'object' && !Object.isFrozen(valor)) {
        Object.freeze(valor);
        for (const clave of Object.keys(valor)) {
            congelarEnProfundidad(valor[clave]);
        }
    }
    return valor;
}

/**
 * Shape que cada `ROULETTE_TYPES[tipo]` expone, construido directo desde la
 * receta agnostica. Cero bridge intermedio.
 *
 * Campos:
 *   id, totalNumbers, apuestas, botonesDelMat   espejos directos de la receta.
 *   sectoresUniformesDeLaRueda                  sectores + arcs + esCero (lector).
 *   matGrid                                     {rows, cols, zerosRow, numbers} (lector).
 *   geometry                                    {rIn, rMid, rOut, ...} (lector).
 *   order                                       array de valores en orden de rueda;
 *                                                los sectores con `top` (etiqueta
 *                                                superior) se expanden a
 *                                                {value, displayValue, label, color}.
 */
function construirConfigDeRueda(idDeRueda) {
    const receta = RECETAS_AGNOSTICAS[idDeRueda];
    if (!receta) throw new Error(`[ROULETTE_TYPES] receta desconocida: "${idDeRueda}".`);

    const order = receta.sectores.map(s => {
        if (s.top != null) {
            return { value: s.valor, displayValue: s.bot, label: s.top, color: '#22bf08' };
        }
        return s.valor;
    });

    return congelarEnProfundidad({
        id: receta.id,
        totalNumbers: receta.sectores.length,
        sectoresUniformesDeLaRueda: sectoresConArcoYesCero(receta),
        order,
        matGrid: matGridDeReceta(receta),
        botonesDelMat: (receta.mat && receta.mat.botones) || [],
        geometry: geometriaDeReceta(receta),
        apuestas: receta.apuestas,
        // ¿Esta rueda tiene mecánica lightning (Mega3000/Red Door)? Espejo booleano de la
        // receta — el contrato lo usa para exponer los overrides lightning SOLO en las
        // ruedas que lo definen (una rueda normal ignora un override lightning).
        tieneLightning: !!receta.lightning,
        // ¿La receta se ofrece en el selector de tipo de rueda del admin? Espejo de la
        // receta con default SEGURO: ausente = true (las ruedas históricas no lo declaran
        // y siguen visibles). Poner `seleccionableEnAdmin: false` en una receta la oculta
        // del admin sin tocar el front — mostrar/ocultar es UN cambio en el SSOT.
        seleccionableEnAdmin: receta.seleccionableEnAdmin !== false,
    });
}

// CONVENCIÓN DE CAJA: claves en MINÚSCULAS, espejando a `RECETAS_AGNOSTICAS`.
// El id de una rueda (`receta.id === 'european'`) es UNA sola string en TODO el
// sistema: receta, ROULETTE_TYPES, contrato, base de datos (`tipo_ruleta`),
// endpoint `wheel-data`, factory, resolver de ceros. Una sola caja elimina la
// traducción `.toUpperCase()` / `.toLowerCase()` esparcida por callers.
export const ROULETTE_TYPES = Object.freeze(
    Object.fromEntries(Object.keys(RECETAS_AGNOSTICAS).map(idDeRueda => [idDeRueda, construirConfigDeRueda(idDeRueda)]))
);

/**
 * Normaliza un id de tipo de rueda a la CLAVE canónica de `ROULETTE_TYPES` —
 * minúsculas. SSOT de normalización: la factory, el resolver de ceros y el
 * cliente del endpoint `wheel-data` deben coincidir en cómo resuelven el tipo,
 * o un mismo sorteo cachearía bajo dos claves distintas. Acepta cualquier caja
 * ('european', 'European', 'EUROPEAN') y devuelve la clave canónica.
 *
 * @param {string} tipoDeRueda - id del tipo, en cualquier caja.
 * @returns {string} clave canónica en minúsculas (puede no existir en la tabla;
 *   el caller decide si eso es fail-fast).
 */
export function normalizarClaveDeTipoDeRueda(tipoDeRueda) {
    return String(tipoDeRueda || '').toLowerCase();
}

/**
 * ¿El tipo de rueda es OPERABLE? Regla de negocio (dueño 2026-07-11): una rueda
 * no visible en el admin (`seleccionableEnAdmin: false` en su receta) NO opera —
 * un sorteo de ese tipo no gira en el scheduler ni aparece en la red de nodos/
 * dispositivos/clientes (evita la rueda "visible pero congelada"). El sorteo NO
 * se borra: queda inerte y reversible (re-habilitar la receta lo revive).
 *
 * FAIL-SAFE ante config corrupta: tipo AUSENTE, vacío o DESCONOCIDO → NO operable.
 * El draw-provider hace fail-fast SIN default sobre tipoRuleta ausente ("sortear
 * europeo por defecto es cobrar contra una rueda que no es la del sorteo") — si
 * acá lo tratáramos como operable, esa config se listaría y el scheduler la
 * dejaría pasar, pero explotaría al sortear: exactamente la rueda "visible pero
 * congelada" que esta regla elimina. Una config sin tipoRuleta no debe girar ni
 * venderse; NO operable es coherente con ese fail-fast.
 *
 * @param {string|null|undefined} tipoRuleta - tipoRuleta de la config del sorteo
 * @returns {boolean}
 */
export function esTipoDeRuedaOperable(tipoRuleta) {
    return motivoDeNoOperabilidadDelTipoDeRueda(tipoRuleta) === null;
}

/**
 * Motivos por los que un tipo de rueda NO es operable. Distinguirlos importa:
 * RUEDA_OCULTA es intencional (receta con seleccionableEnAdmin:false — inerte y
 * reversible, no requiere acción); SIN_TIPO y TIPO_DESCONOCIDO son config
 * corrupta en DB que un humano debe reparar (el sorteo desaparece de venta y
 * scheduler, pero nadie lo decidió).
 */
export const MOTIVOS_TIPO_DE_RUEDA_NO_OPERABLE = Object.freeze({
    SIN_TIPO: 'sin_tipo',
    TIPO_DESCONOCIDO: 'tipo_desconocido',
    RUEDA_OCULTA: 'rueda_oculta',
});

/**
 * Clasifica POR QUÉ un tipo de rueda no es operable.
 *
 * @param {string|null|undefined} tipoRuleta - tipoRuleta de la config del sorteo
 * @returns {string|null} null si es operable; si no, un valor de
 *   MOTIVOS_TIPO_DE_RUEDA_NO_OPERABLE
 */
export function motivoDeNoOperabilidadDelTipoDeRueda(tipoRuleta) {
    if (!tipoRuleta) return MOTIVOS_TIPO_DE_RUEDA_NO_OPERABLE.SIN_TIPO;
    const tipo = ROULETTE_TYPES[normalizarClaveDeTipoDeRueda(tipoRuleta)];
    if (!tipo) return MOTIVOS_TIPO_DE_RUEDA_NO_OPERABLE.TIPO_DESCONOCIDO;
    return tipo.seleccionableEnAdmin ? null : MOTIVOS_TIPO_DE_RUEDA_NO_OPERABLE.RUEDA_OCULTA;
}
